// React
import React, { useContext } from "react";
// Utils
import { CommonContext, ShowNotification } from "config/commonProvider";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { handleErrorCheck } from "helpers/general";
import { useGetGroups } from "hooks";
// Components
import FormBuilder from "components/Common/FormBuilder";
import { FieldSelect, Modal, Spacer, Text } from "components";

interface ConfirmMoveToGroupModalProps {
  showModal: boolean;
  onClose: () => void;
  moveAnimalToGroup: (
    props: {
      group?: string | null | undefined;
    },
    showNotification: ShowNotification,
  ) => void;
  animals: Animal[];
  error?: string;
  requesting: boolean;
  groups: {
    groupsExtended: {
      groups: Group[];
    };
  };
  isRefetchSingleAnimalInfo?: boolean;
}

const ConfirmMoveToGroupModal: React.FC<ConfirmMoveToGroupModalProps> = ({
  error,
  showModal,
  onClose,
  moveAnimalToGroup,
  animals,
  requesting,
}) => {
  const hasGroup = !!animals[0].group;
  const { data: groups } = useGetGroups();

  const groupsOptions = groups.map((item) => ({
    label: item.name,
    value: item.id,
    key: item.id,
  }));

  const { showNotification } = useContext(CommonContext);

  const assignToGroup = (props): void => moveAnimalToGroup(props, showNotification);

  return (
    <FormBuilder
      enableReinitialize
      validateOnChange={false}
      initialValues={{
        group: hasGroup && animals.length === 1 ? animals[0].group.id : "",
      }}
      onSubmit={assignToGroup}
    >
      {({ values, errors, handleChange, handleSubmit }): JSX.Element => (
        <Modal
          actions={{
            primary: {
              id: ELEMENTS_IDS.MOVE_TO_GROUP_MODAL_CONFIRM_BUTTON,
              caption: "Confirm",
              disabled: requesting,
              requesting: requesting,
              onClick: handleSubmit,
            },
            secondary: {
              id: ELEMENTS_IDS.MOVE_TO_GROUP_MODAL_CANCEL_BUTTON,
              caption: "Cancel",
              colour: "grey",
              variant: "hollow",
              onClick: onClose,
            },
          }}
          active={showModal}
          handleClose={onClose}
          subTitle={`Please select a group to ${hasGroup ? "change" : "add"} ${animals
            .map((item) => item.passportNumber)
            .join(", ")} to`}
          title={`${hasGroup ? "Change" : "Add to"} group`}
        >
          <FieldSelect
            error={handleErrorCheck(errors.breeds)}
            inputProps={{
              name: "group",
              value: values.group,
              placeholder: "No group",
              options: groupsOptions,
              onChange: handleChange,
              isClearable: true,
            }}
          />

          {!!error && (
            <>
              <Spacer baselineHeight={2} />

              <Text smaller warning>
                {error}
              </Text>
            </>
          )}
        </Modal>
      )}
    </FormBuilder>
  );
};

export default ConfirmMoveToGroupModal;
